import { motion, AnimatePresence } from "framer-motion";
import { Sparkles } from "lucide-react";

type StudioDraft = {
  content: string;
  style: string;
  description: string;
};

type DraftStyleKey = "a" | "b" | "c" | "d";
const DRAFT_STYLE_KEYS: DraftStyleKey[] = ["a", "b", "c", "d"];

interface StudioDraftBattleProps {
  studioDrafts: StudioDraft[] | null;
  isGenerating: boolean;
  studioActiveDraftIdx: number;
  setStudioActiveDraftIdx: (i: number) => void;
  setStudioDrafts: React.Dispatch<React.SetStateAction<StudioDraft[] | null>>;
  setOutput: React.Dispatch<React.SetStateAction<string | null>>;
  setStudioUndoStack: React.Dispatch<React.SetStateAction<string[]>>;
  setSaved: (v: boolean) => void;
  setFeedbackGiven: (v: "up" | "down" | null) => void;
  fetchStudioCoachTip: (content: string) => void;
  handleFeedbackSilent: (content: string, rating: "up" | "down", draftStyle: string) => Promise<void>;
  outputRef: React.RefObject<HTMLDivElement>;
}

export function StudioDraftBattle({
  studioDrafts,
  isGenerating,
  studioActiveDraftIdx,
  setStudioActiveDraftIdx,
  setStudioDrafts,
  setOutput,
  setStudioUndoStack,
  setSaved,
  setFeedbackGiven,
  fetchStudioCoachTip,
  handleFeedbackSilent,
  outputRef,
}: StudioDraftBattleProps) {
  return (
    <AnimatePresence>
      {studioDrafts && !isGenerating && (
        <motion.div
          ref={outputRef}
          key="studio-drafts"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="rounded-xl overflow-hidden shadow-lg"
          style={{ border: "1px solid rgba(6,182,212,0.25)", boxShadow: "0 0 0 1px rgba(6,182,212,0.12), 0 0 30px rgba(6,182,212,0.08)" }}
        >
          {/* Header with pulse */}
          <div className="flex items-center gap-2 px-3 pt-2.5 pb-2 border-b bg-card"
            style={{ borderColor: "rgba(6,182,212,0.15)" }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
            </span>
            <p className="text-[10px] font-bold text-cyan-400/80 uppercase tracking-widest">
              4 options ready — tap to compare
            </p>
          </div>

          {/* Draft tabs */}
          <div className="flex items-stretch gap-1 p-2 bg-card border-b" style={{ borderColor: "rgba(6,182,212,0.1)" }}>
            {studioDrafts.map((draft, i) => {
              const letter = String.fromCharCode(65 + i);
              const isActive = studioActiveDraftIdx === i;
              return (
                <motion.button
                  key={i}
                  data-testid={`studio-draft-tab-${i}`}
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.07 }}
                  onClick={() => setStudioActiveDraftIdx(i)}
                  className={`flex-1 py-2 px-1.5 rounded-xl transition-all duration-200 text-center border ${
                    isActive
                      ? "bg-cyan-500/10 border-cyan-500/40 text-cyan-300"
                      : "border-border/30 bg-secondary/20 text-muted-foreground hover:border-cyan-500/20 hover:text-foreground hover:bg-secondary/40"
                  }`}
                  style={isActive ? {
                    boxShadow: "0 0 0 1px rgba(6,182,212,0.4), 0 0 16px rgba(6,182,212,0.2)",
                  } : undefined}
                >
                  <span className={`text-sm font-bold block ${isActive ? "text-cyan-300" : ""}`}>
                    {letter}
                  </span>
                  {draft.style && (
                    <span className={`text-[9px] font-semibold uppercase tracking-wide block mt-0.5 leading-tight ${isActive ? "text-cyan-400/80" : "opacity-50"}`}>
                      {draft.style}
                    </span>
                  )}
                  {draft.description && (
                    <span className={`text-[9px] block leading-tight mt-0.5 px-0.5 ${isActive ? "text-cyan-300/70" : "opacity-40"}`}>
                      {draft.description.length > 28 ? draft.description.slice(0, 28) + "…" : draft.description}
                    </span>
                  )}
                </motion.button>
              );
            })}
          </div>

          {/* Active draft content */}
          <div className="px-4 py-3.5 bg-card">
            <textarea
              data-testid="studio-draft-textarea"
              className="w-full min-h-[180px] bg-transparent text-sm leading-relaxed text-foreground resize-y outline-none focus:outline-none"
              value={studioDrafts[studioActiveDraftIdx]?.content ?? ""}
              onChange={(e) => {
                const updated = studioDrafts.map((d, i) =>
                  i === studioActiveDraftIdx ? { ...d, content: e.target.value } : d
                );
                setStudioDrafts(updated);
              }}
            />
          </div>

          {/* Commit button */}
          <div className="px-4 pb-4 pt-1 bg-card">
            <button
              data-testid="studio-use-draft-btn"
              onClick={() => {
                const activeDraft = studioDrafts[studioActiveDraftIdx];
                const chosen = activeDraft?.content ?? "";
                const draftStyle = DRAFT_STYLE_KEYS[studioActiveDraftIdx];
                setOutput(chosen);
                setStudioDrafts(null);
                setStudioUndoStack([]);
                setSaved(false);
                setFeedbackGiven(null);
                if (chosen) fetchStudioCoachTip(chosen);
                if (chosen && draftStyle) void handleFeedbackSilent(chosen, "up", draftStyle);
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-semibold text-sm transition-all"
              style={{ background: "linear-gradient(135deg, hsl(188 90% 42%), hsl(205 88% 52%))", color: "white", boxShadow: "0 0 16px rgba(6,182,212,0.3)" }}
            >
              <Sparkles className="w-4 h-4" />
              {`Use Option ${String.fromCharCode(65 + studioActiveDraftIdx)}`}
            </button>
            <p className="text-center text-[11px] text-muted-foreground/50 mt-2">
              Switch between A–D to compare — edits are preserved per draft.
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
